import { supabase } from '@/lib/supabase';
import { DatabaseRubrique, DatabaseSerie, DatabaseSerieRubrique } from '@/types/database';
import { Rubrique, Serie, Saison, Episode } from '@/types';
import { addImageResizeParams, ImageSizePresets } from '@/utils/imageResizer';
import { viewingProgressCache, CachedViewingProgress } from '@/utils/viewingProgressCache';
import { persistentContentCache } from '@/services/persistentContentCacheService';
import { episodesCacheService } from '@/services/episodesCacheService';
import { networkRetryService } from '@/utils/networkRetry';
import { campaignCache } from '@/services/campaignCacheService';
import { unifiedContentService } from '@/services/unifiedContentService';
import { createScopedLogger } from '@/utils/logging/createScopedLogger';
import { withSupabaseResult } from '@/utils/supabase/withSupabaseResult';

const logger = createScopedLogger('ContentService');

export interface SearchResult {
  id: string;
  title: string;
  description: string;
  type: 'serie' | 'episode';
  thumbnail?: string;
  serieId?: string;
  episodeNumber?: number;
  duration?: number;
  seasonNumber?: number;
}

export class ContentService {
  private static cache: Record<string, Promise<any> | any> = {};

  static clearCache() {
    const timestamp = new Date().toISOString();
    const cacheKeys = Object.keys(this.cache);
    if (cacheKeys.length > 0) {
      cacheKeys.forEach(key => logger.info(`🗄️ [${timestamp}]   - ${key}`));
    }
    this.cache = {};
  }

  private static getCached<T>(key: string): Promise<T> | T | undefined {
    const timestamp = new Date().toISOString();
    const cached = this.cache[key];
    if (cached !== undefined) {
      ;
    } else {
      logger.info(`🗄️ [${timestamp}] ❌ CACHE MISS for key: "${key}"`);
    }
    return cached;
  }

  private static setCache<T>(key: string, value: Promise<T> | T) {
    const timestamp = new Date().toISOString();
    this.cache[key] = value;
  }

  private static shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  private static async checkContentExists(
    tableName: string,
    campaignCountriesLanguagesId: string
  ): Promise<boolean> {
    if (!supabase) return false;

    const { count, error } = await supabase
      .from(tableName)
      .select('*', { count: 'exact', head: true })
      .eq('campaign_countries_languages_id', campaignCountriesLanguagesId);

    if (error) {
      logger.error(`Error checking content existence in ${tableName}:`, error);
      return false;
    }

    return (count || 0) > 0;
  }

  private static async fetchWithFallback<T>(
    tableName: string,
    selectQuery: string,
    campaignCountriesLanguagesId: string | null,
    fallbackCampaignCountriesLanguagesId: string | null,
    additionalFilters?: (query: any) => any,
    languagePreferredCampaignId?: string | null,
    contentMetadataCampaignId?: string | null,
    cacheKey?: string
  ): Promise<T[]> {
    const timestamp = new Date().toISOString();
    if (!supabase) {
      logger.warn(`📚 [${timestamp}] Supabase not configured`);
      return [];
    }

    if (!campaignCountriesLanguagesId) {
      return [];
    }

    // Try cache first if cache key provided
    if (cacheKey) {
      const cached = await persistentContentCache.get<T[]>(cacheKey, campaignCountriesLanguagesId);
      if (cached) {
        logger.info(`📚 [${timestamp}] Using cached data for ${tableName}`);
        return cached;
      }
    }

    // Priority 1: Try content metadata campaign ID first (user's preferred language for content)
    if (contentMetadataCampaignId && contentMetadataCampaignId !== campaignCountriesLanguagesId) {
      const hasContent = await this.checkContentExists(tableName, contentMetadataCampaignId);

      if (hasContent) {
        // Get the language code for META-DATA logging
        const { data: campaignData } = await supabase
          .from('campaign_countries_languages')
          .select('language_code')
          .eq('id', contentMetadataCampaignId)
          .single();

        let contentMetadataQuery = supabase
          .from(tableName)
          .select(selectQuery)
          .eq('campaign_countries_languages_id', contentMetadataCampaignId);

        if (additionalFilters) {
          contentMetadataQuery = additionalFilters(contentMetadataQuery);
        }

        const queryStartTime = Date.now();
        const { data: contentMetadataData, error: contentMetadataError } = await contentMetadataQuery;
        const queryDuration = Date.now() - queryStartTime;

        if (!contentMetadataError && contentMetadataData && contentMetadataData.length > 0) {
          const metadataLanguage = campaignData?.language_code || 'unknown';
          // Log sample titles/descriptions from the results
          if (contentMetadataData.length > 0 && (contentMetadataData[0] as any).title) {
            contentMetadataData.slice(0, 3).forEach((item: any, idx: number) => {
            });
          }

          // Cache series metadata if this is a contents_series query
          if (tableName === 'contents_series' && contentMetadataData.length > 0) {
            const { seriesMetadataCacheService } = await import('@/services/seriesMetadataCacheService');
            await seriesMetadataCacheService.setBulkSeriesMetadata(
              contentMetadataCampaignId,
              contentMetadataData as any[]
            );
          }

          return contentMetadataData as T[];
        } else if (contentMetadataError) {
          logger.error(`📚 [${timestamp}] ❌ Query error:`, contentMetadataError);
        }
      } else {
        ;
      }
    }

    // Priority 2: Try language-preferred campaign ID (kept for backward compatibility)
    if (languagePreferredCampaignId && languagePreferredCampaignId !== campaignCountriesLanguagesId && languagePreferredCampaignId !== contentMetadataCampaignId) {
      const hasContent = await this.checkContentExists(tableName, languagePreferredCampaignId);

      if (hasContent) {
        // Get the language code for META-DATA logging
        const { data: campaignData } = await supabase
          .from('campaign_countries_languages')
          .select('language_code')
          .eq('id', languagePreferredCampaignId)
          .single();

        let languageQuery = supabase
          .from(tableName)
          .select(selectQuery)
          .eq('campaign_countries_languages_id', languagePreferredCampaignId);

        if (additionalFilters) {
          languageQuery = additionalFilters(languageQuery);
        }

        const queryStartTime = Date.now();
        const { data: languageData, error: languageError } = await languageQuery;
        const queryDuration = Date.now() - queryStartTime;


        if (!languageError && languageData && languageData.length > 0) {
          const metadataLanguage = campaignData?.language_code || 'unknown';

          // Log sample titles/descriptions from the results
          if (languageData.length > 0 && (languageData[0] as any).title) {
            languageData.slice(0, 3).forEach((item: any, idx: number) => {
            });
          }

          return languageData as T[];
        } else if (languageError) {
          logger.error(`📚 [${timestamp}] ❌ Query error:`, languageError);
        }
      } else {
        ;
      }
    }

    // Priority 3: Try primary campaign ID
    // Get the language code for primary campaign ID
    const { data: primaryCampaignData } = await supabase
      .from('campaign_countries_languages')
      .select('language_code')
      .eq('id', campaignCountriesLanguagesId)
      .single();

    let query = supabase
      .from(tableName)
      .select(selectQuery)
      .eq('campaign_countries_languages_id', campaignCountriesLanguagesId);

    if (additionalFilters) {
      query = additionalFilters(query);
    }

    const queryStartTime = Date.now();

    // Wrap query with retry logic for network resilience
    let data, error;
    try {
      const result = await networkRetryService.withRetry(
        () => query,
        `${tableName} query`,
        { maxRetries: 2 }
      );
      data = result.data;
      error = result.error;
    } catch (retryError: any) {
      logger.error(`📚 [${timestamp}] ❌ Error fetching from ${tableName} (after retries):`, retryError);

      // Try to return cached data as last resort
      if (cacheKey) {
        logger.info(`📚 [${timestamp}] 🔄 Attempting to use stale cache as fallback...`);
        const staleCache = await persistentContentCache.get<T[]>(cacheKey, campaignCountriesLanguagesId);
        if (staleCache) {
          logger.info(`📚 [${timestamp}] ✅ Returning stale cached data due to network error`);
          return staleCache;
        }
      }

      return [];
    }

    const queryDuration = Date.now() - queryStartTime;

    if (error) {
      logger.error(`📚 [${timestamp}] ❌ Error fetching from ${tableName}:`, error);

      // Try to return cached data as fallback
      if (cacheKey) {
        logger.info(`📚 [${timestamp}] 🔄 Attempting to use cached data as fallback...`);
        const cachedFallback = await persistentContentCache.get<T[]>(cacheKey, campaignCountriesLanguagesId);
        if (cachedFallback) {
          logger.info(`📚 [${timestamp}] ✅ Returning cached data due to query error`);
          return cachedFallback;
        }
      }

      return [];
    }

    if (data && data.length > 0) {
      const metadataLanguage = primaryCampaignData?.language_code || 'unknown';
      // Log sample titles/descriptions from the results
      if (data.length > 0 && (data[0] as any).title) {
        data.slice(0, 3).forEach((item: any, idx: number) => {
        });
      }

      // Cache the successful result
      if (cacheKey) {
        const ttl = persistentContentCache.getTTL(
          tableName.includes('rubrics') ? 'rubriques' :
          tableName.includes('series_episodes') ? 'episodes' : 'series'
        );
        await persistentContentCache.set(cacheKey, campaignCountriesLanguagesId, data, ttl).catch(err =>
          logger.error('Failed to cache data:', err)
        );
      }

      return data as T[];
    }

    if (!fallbackCampaignCountriesLanguagesId) {
      return [];
    }

    // Get the language code for fallback campaign ID
    const { data: fallbackCampaignData } = await supabase
      .from('campaign_countries_languages')
      .select('language_code')
      .eq('id', fallbackCampaignCountriesLanguagesId)
      .single();

    let fallbackQuery = supabase
      .from(tableName)
      .select(selectQuery)
      .eq('campaign_countries_languages_id', fallbackCampaignCountriesLanguagesId);

    if (additionalFilters) {
      fallbackQuery = additionalFilters(fallbackQuery);
    }

    const fallbackQueryStartTime = Date.now();

    // Wrap fallback query with retry logic
    let fallbackData, fallbackError;
    try {
      const result = await networkRetryService.withRetry(
        () => fallbackQuery,
        `${tableName} fallback query`,
        { maxRetries: 2 }
      );
      fallbackData = result.data;
      fallbackError = result.error;
    } catch (retryError: any) {
      logger.error(`📚 [${timestamp}] ❌ Error fetching from ${tableName} with fallback (after retries):`, retryError);

      // Try cached data as absolute last resort
      if (cacheKey) {
        logger.info(`📚 [${timestamp}] 🔄 Attempting to use cached data after fallback failure...`);
        const cachedData = await persistentContentCache.get<T[]>(cacheKey, campaignCountriesLanguagesId);
        if (cachedData) {
          logger.info(`📚 [${timestamp}] ✅ Returning cached data after all network attempts failed`);
          return cachedData;
        }
      }

      return [];
    }

    const fallbackQueryDuration = Date.now() - fallbackQueryStartTime;

    if (fallbackError) {
      logger.error(`📚 [${timestamp}] ❌ Error fetching from ${tableName} with fallback:`, fallbackError);

      // Try cached data as last resort
      if (cacheKey) {
        logger.info(`📚 [${timestamp}] 🔄 Attempting to use cached data after fallback error...`);
        const cachedData = await persistentContentCache.get<T[]>(cacheKey, campaignCountriesLanguagesId);
        if (cachedData) {
          logger.info(`📚 [${timestamp}] ✅ Returning cached data after fallback query error`);
          return cachedData;
        }
      }

      return [];
    }

    if (fallbackData && fallbackData.length > 0) {
      const metadataLanguage = fallbackCampaignData?.language_code || 'unknown';
      // Log sample titles/descriptions from the results
      if (fallbackData.length > 0 && (fallbackData[0] as any).title) {
        fallbackData.slice(0, 3).forEach((item: any, idx: number) => {
          ;
        });
      }

      // Cache the fallback result
      if (cacheKey) {
        const ttl = persistentContentCache.getTTL(
          tableName.includes('rubrics') ? 'rubriques' :
          tableName.includes('series_episodes') ? 'episodes' : 'series'
        );
        await persistentContentCache.set(cacheKey, campaignCountriesLanguagesId, fallbackData, ttl).catch(err =>
          logger.error('Failed to cache fallback data:', err)
        );
      }
    } else {
      logger.info(`📚 [${timestamp}] ⚠️ No data found in ${tableName} even with French fallback`);
    }

    return (fallbackData || []) as T[];
  }
  // Search across series, episodes, and rubriques
  static async search(campaignCountriesLanguagesId: string | null, query: string): Promise<SearchResult[]> {
    if (!campaignCountriesLanguagesId) {
      logger.info('ContentService.search: No campaign ID, returning empty results');
      return [];
    }

    if (!query.trim()) {
      return [];
    }

    if (!supabase) {
      logger.warn('Supabase not configured');
      return [];
    }

    try {
      logger.info('Searching with query:', query);
      const searchTerm = `%${query.toLowerCase()}%`;
      const results: SearchResult[] = [];

      // Search in contents_series (title and description)
      // First, search for series with title matches
      const { data: titleMatches, error: titleError } = await withSupabaseResult<DatabaseSerie[]>(
        supabase
          .from('contents_series')
          .select('*')
          .eq('campaign_countries_languages_id', campaignCountriesLanguagesId)
          .ilike('title', searchTerm),
        { operation: 'content.search.titleMatches', logger, fallback: [] }
      );

      if (titleError) {
        logger.error('Error searching series titles:', titleError.message);
      } else if (titleMatches) {
        titleMatches.forEach((serie: DatabaseSerie) => {
          results.push({
            id: serie.serie_id.toString(),
            title: serie.title,
            description: serie.description || 'No description available',
            type: 'serie',
            thumbnail: addImageResizeParams(serie.url_covers, ImageSizePresets.searchThumbnail, `Search Result: ${serie.title}`)
          });
        });
      }

      // Then, search for series with description matches (excluding already found titles)
      const titleMatchIds = (titleMatches || []).map(serie => serie.serie_id);
      
      const { data: descriptionMatches, error: descriptionError } = await withSupabaseResult<DatabaseSerie[]>(
        supabase
          .from('contents_series')
          .select('*')
          .eq('campaign_countries_languages_id', campaignCountriesLanguagesId)
          .ilike('description', searchTerm)
          .not('serie_id', 'in', `(${titleMatchIds.join(',')})`),
        { operation: 'content.search.descriptionMatches', logger, fallback: [] }
      );

      if (descriptionError) {
        logger.error('Error searching series descriptions:', descriptionError.message);
      } else if (descriptionMatches) {
        descriptionMatches.forEach((serie: DatabaseSerie) => {
          results.push({
            id: serie.serie_id.toString(),
            title: serie.title,
            description: serie.description || 'No description available',
            type: 'serie',
            thumbnail: addImageResizeParams(serie.url_covers, ImageSizePresets.searchThumbnail, `Search Result: ${serie.title}`)
          });
        });
      }

      // Search in contents_series_episodes (title and description)

      // Search in contents_rubrics (rubric_name)
      // Note: Category/rubrique search removed as per requirements

      logger.info('Search results:', results);
      return results;
    } catch (err) {
      logger.error('Unexpected error during search:', err);
      return [];
    }
  }

  // Récupérer les séries les plus vues pour les top charts
  static async getTopViewedSeries(
    campaignCountriesLanguagesId: string | null,
    limit: number = 3,
    fallbackCampaignCountriesLanguagesId: string | null = null
  ): Promise<Serie[]> {
    if (!campaignCountriesLanguagesId) {
      logger.info('ContentService.getTopViewedSeries: No campaign ID, returning empty results');
      return [];
    }

    const cacheKey = `topViewed-${campaignCountriesLanguagesId}-${limit}`;
    const cached = this.getCached<Serie[]>(cacheKey);
    if (cached) return cached;

    const fetchPromise = (async () => {
      if (!supabase) {
        logger.warn('Supabase not configured');
        const result: Serie[] = [];
        this.setCache(cacheKey, result);
        return result;
      }

      try {
        logger.info('Fetching top viewed series from Supabase...');

        // Get most viewed series from user_viewing_progress
        const { data: viewingData, error: viewingError } = await supabase
          .from('user_viewing_progress')
          .select('content_id, content_type')
          .eq('content_type', 'series')
          .order('updated_at', { ascending: false });

        if (viewingError) {
          logger.error('Error fetching viewing progress:', viewingError);
          const fallback: Serie[] = [];
          this.setCache(cacheKey, fallback);
          return fallback;
        }

        if (!viewingData || viewingData.length === 0) {
          logger.info('No viewing data found, using regular series');
          const fallback = await this.getSeries(campaignCountriesLanguagesId, fallbackCampaignCountriesLanguagesId).then(series => series.slice(0, limit));
          this.setCache(cacheKey, fallback);
          return fallback;
        }

        // Count views per series
        const viewCounts = viewingData.reduce((acc, item) => {
          acc[item.content_id] = (acc[item.content_id] || 0) + 1;
          return acc;
        }, {} as Record<number, number>);

        // Get top series IDs
        const topSeriesIds = Object.entries(viewCounts)
          .sort(([,a], [,b]) => b - a)
          .slice(0, limit)
          .map(([id]) => parseInt(id));

        if (topSeriesIds.length === 0) {
          const fallback: Serie[] = [];
          this.setCache(cacheKey, fallback);
          return fallback;
        }

        // Fetch series details with fallback
        const [seriesData, seasonCounts] = await Promise.all([
          this.fetchWithFallback<DatabaseSerie>(
            'contents_series',
            '*',
            campaignCountriesLanguagesId,
            fallbackCampaignCountriesLanguagesId,
            (query) => query.in('serie_id', topSeriesIds)
          ),
          this.fetchSeasonCounts(topSeriesIds),
        ]);

        const mapped = this.mapSeriesToInterface(seriesData || [], seasonCounts);

        // Cache series metadata for instant access
        if (seriesData && seriesData.length > 0 && campaignCountriesLanguagesId) {
          const { seriesMetadataCacheService } = await import('@/services/seriesMetadataCacheService');
          const metadataList = seriesData.map((serie: DatabaseSerie) => ({
            serie_id: serie.serie_id,
            title: serie.title,
            description: serie.description,
            url_covers: serie.url_covers,
            campaign_countries_languages_id: serie.campaign_countries_languages_id
          }));

          await seriesMetadataCacheService.setBulkSeriesMetadata(
            campaignCountriesLanguagesId,
            metadataList
          );
        }

        this.setCache(cacheKey, mapped);
        return mapped;
      } catch (err) {
        logger.error('Unexpected error fetching top viewed series:', err);
        const fallback: Serie[] = [];
        this.setCache(cacheKey, fallback);
        return fallback;
      }
    })();

    this.setCache(cacheKey, fetchPromise);
    return fetchPromise;
  }

  // Récupérer le top 3 configuré depuis homepage_covers via Edge Function
  static async getHomepageTop3(
    campaignCountriesLanguagesId: string | null,
    contentMetadataCampaignId: string | null = null
  ): Promise<Serie[]> {
    if (!campaignCountriesLanguagesId) {
      logger.info('ContentService.getHomepageTop3: No campaign ID, returning empty results');
      return [];
    }

    // Cache key includes the content-metadata campaign so each selected language
    // is cached separately (otherwise a language switch would serve stale titles).
    const cacheKey = `homepageTop3_v3-${campaignCountriesLanguagesId}-${contentMetadataCampaignId ?? 'none'}`;
    const cached = this.getCached<Serie[]>(cacheKey);
    if (cached) return cached;

    const fetchPromise = (async () => {
      try {
        logger.info('🎯 [ContentService.getHomepageTop3] Calling Edge Function getTop3...');
        logger.info('🎯 [ContentService.getHomepageTop3] Campaign ID:', campaignCountriesLanguagesId);

        // Get Supabase URL from environment
        const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL;
        if (!supabaseUrl) {
          logger.error('🎯 [ContentService.getHomepageTop3] EXPO_PUBLIC_SUPABASE_URL not configured');
          const fallback: Serie[] = [];
          this.setCache(cacheKey, fallback);
          return fallback;
        }

        // Call Edge Function with timeout to prevent hanging. Pass the content-metadata
        // campaign so titles/descriptions follow the user's selected language (the edge
        // function falls back to the primary campaign per-series when a row is missing).
        const top3Params = new URLSearchParams({ campaign_countries_languages_id: campaignCountriesLanguagesId });
        if (contentMetadataCampaignId && contentMetadataCampaignId !== campaignCountriesLanguagesId) {
          top3Params.set('content_metadata_campaign_id', contentMetadataCampaignId);
        }
        const edgeFunctionUrl = `${supabaseUrl}/functions/v1/getTop3?${top3Params.toString()}`;
        const anonKey = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY || '';
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          logger.info('🎯 [ContentService.getHomepageTop3] Request timeout after 5000ms, aborting...');
          controller.abort();
        }, 5000);

        let response: Response;
        try {
          response = await fetch(edgeFunctionUrl, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${anonKey}`,
              'apikey': anonKey,
            },
            signal: controller.signal,
          });
        } catch (fetchErr: any) {
          clearTimeout(timeoutId);
          if (fetchErr?.name === 'AbortError') {
            logger.info('🎯 [ContentService.getHomepageTop3] Request aborted due to timeout');
          } else {
            logger.error('🎯 [ContentService.getHomepageTop3] Fetch error:', fetchErr);
          }
          const fallback: Serie[] = [];
          this.setCache(cacheKey, fallback);
          return fallback;
        }
        clearTimeout(timeoutId);

        if (!response.ok) {
          logger.error('🎯 [ContentService.getHomepageTop3] Edge Function error:', response.status, response.statusText);
          const fallback: Serie[] = [];
          this.setCache(cacheKey, fallback);
          return fallback;
        }

        const result = await response.json();
        logger.info('🎯 [ContentService.getHomepageTop3] Edge Function response:', result);

        // Check if we got data
        if (!result.success || !result.data || result.data.length === 0) {
          logger.info('🎯 [ContentService.getHomepageTop3] No homepage top 3 data found, returning empty array');
          const fallback: Serie[] = [];
          this.setCache(cacheKey, fallback);
          return fallback;
        }

        // Map the response to Serie interface, filtering out any items with null/undefined serie_id
        // The response contains: { serie_id, position, cover_url, title, description }
        const validItems = result.data.filter((item: any) =>
          item && item.serie_id !== null && item.serie_id !== undefined
        );

        if (validItems.length === 0) {
          logger.info('🎯 [ContentService.getHomepageTop3] No valid items after filtering null serie_ids');
          const fallback: Serie[] = [];
          this.setCache(cacheKey, fallback);
          return fallback;
        }

        const mapped: Serie[] = validItems
          .map((item: any) => ({
            id: item.serie_id.toString(),
            title: item.title || 'Unknown',
            description: item.description || '',
            thumbnail: item.cover_url || item.url_covers || '',
            position: item.position,
          }))
          .filter((s: Serie) => s.thumbnail && s.thumbnail.trim().length > 0);

        logger.info('🎯 [ContentService.getHomepageTop3] Successfully mapped', mapped.length, 'series');
        this.setCache(cacheKey, mapped);
        return mapped;
      } catch (err) {
        logger.error('🎯 [ContentService.getHomepageTop3] Unexpected error:', err);
        const fallback: Serie[] = [];
        this.setCache(cacheKey, fallback);
        return fallback;
      }
    })();

    this.setCache(cacheKey, fetchPromise);
    return fetchPromise;
  }

  // Récupérer toutes les rubriques
  static async getRubriques(
    campaignCountriesLanguagesId: string | null,
    fallbackCampaignCountriesLanguagesId: string | null = null,
    languagePreferredCampaignId: string | null = null,
    contentMetadataCampaignId: string | null = null
  ): Promise<Rubrique[]> {
    const timestamp = new Date().toISOString();
    if (!campaignCountriesLanguagesId) {
      return [];
    }

    const cacheKey = `rubriques-${contentMetadataCampaignId || languagePreferredCampaignId || campaignCountriesLanguagesId}`;

    const cached = this.getCached<Rubrique[]>(cacheKey);
    if (cached) {
      return cached;
    }

    const fetchPromise = (async () => {
      try {
        logger.info(`🔍 [ContentService] getRubriques: Fetching from Supabase for campaign ${campaignCountriesLanguagesId}`);

        const data = await this.fetchWithFallback<DatabaseRubrique>(
          'contents_rubrics',
          '*',
          campaignCountriesLanguagesId,
          fallbackCampaignCountriesLanguagesId,
          undefined,
          languagePreferredCampaignId,
          contentMetadataCampaignId,
          cacheKey
        );

        if (!data || data.length === 0) {
          logger.info(`🔍 [ContentService] getRubriques: Supabase returned empty data for campaign ${campaignCountriesLanguagesId}`);
          const result: Rubrique[] = [];
          this.setCache(cacheKey, result);
          return result;
        }

        logger.info(`🔍 [ContentService] getRubriques: Supabase returned ${data.length} rubriques for campaign ${campaignCountriesLanguagesId}`);

        const mapped = data.map((item: DatabaseRubrique) => ({
          id: item.id_rubric.toString(),
          name: item.rubric_name,
          description: item.rubric_name,
          thumbnail: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176'
        }));
        this.setCache(cacheKey, mapped);
        return mapped;
      } catch (err) {
        logger.error('Unexpected error fetching rubriques:', err);
        const result: Rubrique[] = [];
        this.setCache(cacheKey, result);
        return result;
      }
    })();

    this.setCache(cacheKey, fetchPromise);
    return fetchPromise;
  }

  // Récupérer toutes les séries
  static async getSeries(
    campaignCountriesLanguagesId: string | null,
    fallbackCampaignCountriesLanguagesId: string | null = null,
    languagePreferredCampaignId: string | null = null,
    contentMetadataCampaignId: string | null = null
  ): Promise<Serie[]> {
    const timestamp = new Date().toISOString();
    if (!campaignCountriesLanguagesId) {
      return [];
    }

    const cacheKey = `series-${contentMetadataCampaignId || languagePreferredCampaignId || campaignCountriesLanguagesId}`;
    const cached = this.getCached<Serie[]>(cacheKey);
    if (cached) {
      return cached;
    }

    const fetchPromise = (async () => {
      try {
        logger.info(`🔍 [ContentService] getSeries: Fetching from Supabase for campaign ${campaignCountriesLanguagesId}`);

        const data = await this.fetchWithFallback<DatabaseSerie>(
          'contents_series',
          '*',
          campaignCountriesLanguagesId,
          fallbackCampaignCountriesLanguagesId,
          undefined,
          languagePreferredCampaignId,
          contentMetadataCampaignId,
          cacheKey
        );

        if (!data || data.length === 0) {
          logger.info(`🔍 [ContentService] getSeries: Supabase returned empty data for campaign ${campaignCountriesLanguagesId}`);
          const result: Serie[] = [];
          this.setCache(cacheKey, result);
          return result;
        }

        logger.info(`🔍 [ContentService] getSeries: Supabase returned ${data.length} series for campaign ${campaignCountriesLanguagesId}`);

        const seriesIds = data.map((s: DatabaseSerie) => s.serie_id);
        const seasonCounts = await this.fetchSeasonCounts(seriesIds);
        const mapped = this.mapSeriesToInterface(data, seasonCounts);
        this.setCache(cacheKey, mapped);

        // OPTIMIZATION: Cache series metadata for instant access when navigating to series pages
        // This eliminates database queries when user clicks on a series from homepage
        if (data.length > 0 && campaignCountriesLanguagesId) {
          const { seriesMetadataCacheService } = await import('@/services/seriesMetadataCacheService');
          const metadataList = data.map((serie: DatabaseSerie) => ({
            serie_id: serie.serie_id,
            title: serie.title,
            description: serie.description,
            url_covers: serie.url_covers,
            campaign_countries_languages_id: serie.campaign_countries_languages_id
          }));

          await seriesMetadataCacheService.setBulkSeriesMetadata(
            campaignCountriesLanguagesId,
            metadataList
          );
          logger.info(`📺 [SERIES-METADATA] Cached metadata for ${metadataList.length} series`);
        }

        if (mapped.length > 0) {
          const bulkPrefetchStart = Date.now();
          logger.info(`📺 [SERIES-PREFETCH] Starting bulk episode prefetch for ${mapped.length} series...`);

          const allSeriesIds = mapped.map(serie => serie.id);

          (async () => {
            const priorityIds = await this.collectPrioritySeriesIds(campaignCountriesLanguagesId);
            episodesCacheService.setPrioritySeriesIds(priorityIds);

            let orderedSeriesIds = allSeriesIds;
            if (priorityIds.length > 0) {
              const prioritySet = new Set(priorityIds);
              const prioritySeries = allSeriesIds.filter(id => prioritySet.has(id));
              const otherSeries = allSeriesIds.filter(id => !prioritySet.has(id));
              orderedSeriesIds = [...prioritySeries, ...otherSeries];
            }

            return this.fetchAllEpisodesForMultipleSeries(campaignCountriesLanguagesId, orderedSeriesIds);
          })()
            .then(async (episodesMap) => {
              if (episodesMap.size > 0) {
                // Cache all episodes in batch
                const cacheResult = await episodesCacheService.cacheEpisodesBatch(
                  campaignCountriesLanguagesId,
                  episodesMap
                );

                const bulkPrefetchDuration = Date.now() - bulkPrefetchStart;
                logger.info(`📺 [SERIES-PREFETCH] ========================================`);
                logger.info(`📺 [SERIES-PREFETCH] BULK PREFETCH COMPLETED`);
                logger.info(`📺 [SERIES-PREFETCH] ========================================`);
                logger.info(`📺 [SERIES-PREFETCH] Series requested: ${mapped.length}`);
                logger.info(`📺 [SERIES-PREFETCH] Series with episodes: ${episodesMap.size}`);
                logger.info(`📺 [SERIES-PREFETCH] Series cached successfully: ${cacheResult.successful}`);
                logger.info(`📺 [SERIES-PREFETCH] Total episodes cached: ${cacheResult.totalEpisodes}`);
                logger.info(`📺 [SERIES-PREFETCH] Total time: ${Math.round(bulkPrefetchDuration / 1000)}s`);

                // Verification: Check if all series are covered
                const coveragePercent = Math.round((episodesMap.size / mapped.length) * 100);
                if (coveragePercent === 100) {
                  logger.info(`📺 [SERIES-PREFETCH] ✅ VERIFICATION: 100% coverage - All ${mapped.length} series have cached episodes`);
                } else {
                  logger.warn(`📺 [SERIES-PREFETCH] ⚠️ VERIFICATION: ${coveragePercent}% coverage - ${mapped.length - episodesMap.size} series missing`);
                  const missingSeriesIds = allSeriesIds.filter(id => !episodesMap.has(parseInt(id)));
                  logger.warn(`📺 [SERIES-PREFETCH] Missing series IDs:`, missingSeriesIds.slice(0, 10), missingSeriesIds.length > 10 ? `... and ${missingSeriesIds.length - 10} more` : '');
                }

                if (cacheResult.failed > 0) {
                  logger.warn(`📺 [SERIES-PREFETCH] ⚠️ Failed to cache ${cacheResult.failed} series`);
                }
                logger.info(`📺 [SERIES-PREFETCH] ========================================`);
              } else {
                logger.info(`📺 [SERIES-PREFETCH] ℹ️ No episodes found for any series`);
              }
            })
            .catch((error) => {
              const bulkPrefetchDuration = Date.now() - bulkPrefetchStart;
              logger.error(`📺 [SERIES-PREFETCH] ❌ Bulk prefetch failed after ${bulkPrefetchDuration}ms:`, error);
              // Don't throw - series are already cached and available
            });
        }

        return mapped;
      } catch (err) {
        logger.error('Unexpected error fetching series:', err);
        const result: Serie[] = [];
        this.setCache(cacheKey, result);
        return result;
      }
    })();

    this.setCache(cacheKey, fetchPromise);
    return fetchPromise;
  }

  static async getSeriesById(seriesId: string | number, campaignCountriesLanguagesId?: string | null): Promise<Serie | null> {
    const normalizedSeriesId = String(seriesId);

    if (!supabase) {
      logger.warn('Supabase not configured');
      return null;
    }

    try {
      if (campaignCountriesLanguagesId) {
        const { seriesMetadataCacheService } = await import('@/services/seriesMetadataCacheService');
        const cached = await seriesMetadataCacheService.getSeriesMetadata(campaignCountriesLanguagesId, normalizedSeriesId);

        if (cached) {
          logger.info(`🎬 [GET-SERIES-BY-ID] Using cached series for: ${normalizedSeriesId}`);
          return this.mapSeriesToInterface([cached])[0];
        }
      }

      logger.info(`🎬 [GET-SERIES-BY-ID] Fetching series from database for: ${normalizedSeriesId}`);
      let query = supabase
        .from('contents_series')
        .select('*')
        .eq('serie_id', normalizedSeriesId);

      if (campaignCountriesLanguagesId) {
        query = query.eq('campaign_countries_languages_id', campaignCountriesLanguagesId);
      }

      const { data, error } = await query
        .limit(1)
        .maybeSingle();

      if (error) {
        logger.error('Error fetching series by ID:', error);
        return null;
      }

      if (!data) {
        return null;
      }

      if (campaignCountriesLanguagesId && data) {
        const { seriesMetadataCacheService } = await import('@/services/seriesMetadataCacheService');
        await seriesMetadataCacheService.setSeriesMetadata(campaignCountriesLanguagesId, data);
      }

      return this.mapSeriesToInterface([data])[0];
    } catch (err) {
      logger.error('Unexpected error fetching series by ID:', err);
      return null;
    }
  }

  // Récupérer les séries d'une rubrique
  static async getSeriesByRubrique(
    campaignCountriesLanguagesId: string | null,
    rubriqueId: string,
    fallbackCampaignCountriesLanguagesId: string | null = null,
    languagePreferredCampaignId: string | null = null,
    contentMetadataCampaignId: string | null = null
  ): Promise<Serie[]> {
    if (!campaignCountriesLanguagesId) {
      return [];
    }

    const cacheKey = `seriesByRubrique-${contentMetadataCampaignId || languagePreferredCampaignId || campaignCountriesLanguagesId}-${rubriqueId}`;
    const cached = this.getCached<Serie[]>(cacheKey);
    if (cached) return cached;

    const fetchPromise = (async () => {
      if (!supabase) {
        logger.warn('Supabase not configured');
        const result: Serie[] = [];
        this.setCache(cacheKey, result);
        return result;
      }

      try {
        // First get series IDs for this rubrique with fallback
        const rubriqueData = await this.fetchWithFallback<DatabaseSerieRubrique>(
          'contents_series_rubrics',
          'serie_id',
          campaignCountriesLanguagesId,
          fallbackCampaignCountriesLanguagesId,
          (query) => query.eq('id_rubric', parseInt(rubriqueId)),
          languagePreferredCampaignId,
          contentMetadataCampaignId,
          `${cacheKey}_rubrique_ids`
        );

        if (!rubriqueData || rubriqueData.length === 0) {
          logger.info('No series found for rubrique');
          const result: Serie[] = [];
          this.setCache(cacheKey, result);
          return result;
        }

        const seriesIds = rubriqueData.map(item => item.serie_id);

        const [seriesData, seasonCounts] = await Promise.all([
          this.fetchWithFallback<DatabaseSerie>(
            'contents_series',
            '*',
            campaignCountriesLanguagesId,
            fallbackCampaignCountriesLanguagesId,
            (query) => query.in('serie_id', seriesIds),
            languagePreferredCampaignId,
            contentMetadataCampaignId,
            cacheKey
          ),
          this.fetchSeasonCounts(seriesIds),
        ]);

        const mapped = this.mapSeriesToInterface(seriesData || [], seasonCounts);

        // Cache series metadata for instant access
        if (seriesData && seriesData.length > 0 && campaignCountriesLanguagesId) {
          const { seriesMetadataCacheService } = await import('@/services/seriesMetadataCacheService');
          const metadataList = seriesData.map((serie: DatabaseSerie) => ({
            serie_id: serie.serie_id,
            title: serie.title,
            description: serie.description,
            url_covers: serie.url_covers,
            campaign_countries_languages_id: serie.campaign_countries_languages_id
          }));

          await seriesMetadataCacheService.setBulkSeriesMetadata(
            campaignCountriesLanguagesId,
            metadataList
          );
        }

        const shuffled = this.shuffleArray(mapped);
        this.setCache(cacheKey, shuffled);
        return shuffled;
      } catch (err) {
        logger.error('Unexpected error fetching series by rubrique:', err);
        const result: Serie[] = [];
        this.setCache(cacheKey, result);
        return result;
      }
    })();

    this.setCache(cacheKey, fetchPromise);
    return fetchPromise;
  }

  static async getSaisonsBySerie(campaignCountriesLanguagesId: string | null, serieId: string): Promise<Saison[]> {
    if (!campaignCountriesLanguagesId) {
      logger.info('ContentService.getSaisonsBySerie: No campaign ID, returning empty results');
      return [];
    }

    if (!supabase) {
      logger.warn('Supabase not configured');
      return [];
    }

    try {
      logger.info('Fetching saisons from unified tables...');

      const { data, error } = await supabase
        .from('unified_contents_series_episodes')
        .select('season_id, season_position')
        .eq('series_id', parseInt(serieId))
        .order('season_position', { ascending: true });

      if (error) {
        logger.error('Error fetching saisons:', error);
        return [];
      }

      if (!data || data.length === 0) {
        logger.info('No saisons found');
        return [];
      }

      const seasonsMap = new Map();
      for (const episode of data) {
        if (!seasonsMap.has(episode.season_id)) {
          seasonsMap.set(episode.season_id, {
            id: episode.season_id.toString(),
            number: episode.season_position || 1,
            title: `Season ${episode.season_position || 1}`,
            serieId: serieId,
            totalEpisodes: 0
          });
        }
        seasonsMap.get(episode.season_id).totalEpisodes++;
      }

      return Array.from(seasonsMap.values());
    } catch (err) {
      logger.error('Unexpected error fetching saisons:', err);
      return [];
    }
  }

  static async getEpisodesBySaison(campaignCountriesLanguagesId: string | null, saisonId: string, freeEpisodesCount: number = 3): Promise<Episode[]> {
    if (!campaignCountriesLanguagesId) {
      logger.info('ContentService.getEpisodesBySaison: No campaign ID, returning empty results');
      return [];
    }

    try {
      const languageCode = campaignCache.getLanguageCode(campaignCountriesLanguagesId);
      const numericSeasonId = parseInt(saisonId);

      const unifiedEpisodes = await unifiedContentService.getEpisodesBySeason(numericSeasonId, languageCode);

      if (!unifiedEpisodes || unifiedEpisodes.length === 0) {
        logger.info(`No episodes found for season ${saisonId}`);
        return [];
      }

      const seriesId = unifiedEpisodes[0].series_id;
      const allSeriesEpisodes = await unifiedContentService.getEpisodesBySeries(seriesId, languageCode);

      const freeEpisodeIds = new Set(
        allSeriesEpisodes
          .slice(0, freeEpisodesCount)
          .map(ep => ep.episode_id)
      );

      return unifiedEpisodes.map(ep => ({
        id: ep.episode_id.toString(),
        title: ep.title,
        description: ep.description || `Episode ${ep.episode_position} description`,
        thumbnail: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176',
        duration: ep.duration || 900,
        saisonId: saisonId,
        episodeNumber: ep.episode_position,
        is_free: freeEpisodeIds.has(ep.episode_id),
        video_url: ep.url_streaming_no_drm,
        url_streaming_drm: ep.url_streaming_drm,
        seriesId: ep.series_id.toString(),
        seasonNumber: ep.season_position || 1,
        seasonId: ep.season_id.toString()
      }));
    } catch (err) {
      logger.error('Unexpected error fetching episodes:', err);
      return [];
    }
  }

  static async getEpisodeById(campaignCountriesLanguagesId: string | null, episodeId: string, freeEpisodesCount: number = 3): Promise<Episode | null> {
    if (!campaignCountriesLanguagesId) {
      logger.info('ContentService.getEpisodeById: No campaign ID, returning null');
      return null;
    }

    if (!supabase) {
      logger.warn('Supabase not configured');
      return null;
    }

    try {
      const languageCode = campaignCache.getLanguageCode(campaignCountriesLanguagesId);
      const numericEpisodeId = parseInt(episodeId);

      const unifiedEpisode = await unifiedContentService.getEpisodeById(numericEpisodeId, languageCode);

      if (!unifiedEpisode) {
        return null;
      }

      const allSeriesEpisodes = await unifiedContentService.getEpisodesBySeries(unifiedEpisode.series_id, languageCode);
      const freeEpisodeIds = new Set(
        allSeriesEpisodes.slice(0, freeEpisodesCount).map(ep => ep.episode_id)
      );
      const isFree = freeEpisodeIds.has(unifiedEpisode.episode_id);

      const { data: seriesData } = await supabase
        .from('contents_series')
        .select('url_covers, title')
        .eq('campaign_countries_languages_id', campaignCountriesLanguagesId)
        .eq('serie_id', unifiedEpisode.series_id)
        .maybeSingle();

      const seriesCover = seriesData?.url_covers || '';

      return {
        id: unifiedEpisode.episode_id.toString(),
        title: unifiedEpisode.title,
        description: unifiedEpisode.description || `Episode ${unifiedEpisode.episode_position} description`,
        thumbnail: addImageResizeParams(seriesCover, ImageSizePresets.serieCardVertical, `Episode: ${unifiedEpisode.title}`),
        duration: unifiedEpisode.duration || 900,
        saisonId: unifiedEpisode.season_id.toString(),
        episodeNumber: unifiedEpisode.episode_position,
        is_free: isFree,
        video_url: unifiedEpisode.url_streaming_no_drm,
        url_streaming_drm: unifiedEpisode.url_streaming_drm,
        seriesId: unifiedEpisode.series_id.toString(),
        seasonNumber: unifiedEpisode.season_position || 1,
        seasonId: unifiedEpisode.season_id.toString(),
        series_thumbnail: addImageResizeParams(seriesCover, ImageSizePresets.serieCardVertical, `Series Cover: ${seriesData?.title || 'Unknown'}`)
      };
    } catch (err) {
      logger.error('Unexpected error fetching episode:', err);
      return null;
    }
  }

  static async getFirstSeasonIdForSeries(campaignCountriesLanguagesId: string | null, seriesId: string): Promise<{
    seasonId: string;
    seriesId: string;
    seasonPosition: number;
  } | null> {
    if (!campaignCountriesLanguagesId) {
      logger.info('ContentService.getFirstSeasonIdForSeries: No campaign ID, returning null');
      return null;
    }

    if (!supabase) {
      logger.warn('Supabase not configured');
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('unified_contents_series_episodes')
        .select('season_id, season_position')
        .eq('series_id', parseInt(seriesId))
        .order('season_position', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (error) {
        logger.error('Error fetching first season:', error);
        return null;
      }

      if (!data) {
        logger.info('No season found for series');
        return null;
      }

      return {
        seasonId: data.season_id.toString(),
        seriesId: seriesId,
        seasonPosition: data.season_position || 1
      };
    } catch (err) {
      logger.error('Unexpected error fetching first season:', err);
      return null;
    }
  }

  // Get series that have at least one free episode
  // With the new logic, any series with at least one episode has free episodes (first N episodes are free)
  static async getSeriesWithFreeEpisodes(
    campaignCountriesLanguagesId: string | null,
    fallbackCampaignCountriesLanguagesId: string | null = null
  ): Promise<Serie[]> {
    if (!campaignCountriesLanguagesId) {
      logger.info('ContentService.getSeriesWithFreeEpisodes: No campaign ID, returning empty results');
      return [];
    }

    const cacheKey = `freeSeries-${campaignCountriesLanguagesId}`;
    const cached = this.getCached<Serie[]>(cacheKey);
    if (cached) return cached;

    const fetchPromise = (async () => {
      if (!supabase) {
        logger.warn('Supabase not configured');
        const result: Serie[] = [];
        this.setCache(cacheKey, result);
        return result;
      }

      try {
        logger.info('Fetching series with free episodes from Supabase...');

        const { data: episodesData, error: episodesError } = await supabase
          .from('unified_contents_series_episodes')
          .select('series_id');

        if (episodesError) {
          logger.error('Error fetching episodes for series:', episodesError);
          const result: Serie[] = [];
          this.setCache(cacheKey, result);
          return result;
        }

        if (!episodesData || episodesData.length === 0) {
          logger.info('No episodes found');
          this.setCache(cacheKey, []);
          return [];
        }

        // Extract unique series IDs
        const seriesIds = [...new Set(episodesData.map(item => item.series_id))];

        if (seriesIds.length === 0) {
          this.setCache(cacheKey, []);
          return [];
        }

        logger.info(`Found ${seriesIds.length} series with episodes (and therefore free episodes)`);

        const [seriesData, seasonCounts] = await Promise.all([
          this.fetchWithFallback<DatabaseSerie>(
            'contents_series',
            '*',
            campaignCountriesLanguagesId,
            fallbackCampaignCountriesLanguagesId,
            (query) => query.in('serie_id', seriesIds)
          ),
          this.fetchSeasonCounts(seriesIds),
        ]);

        const mapped = this.mapSeriesToInterface(seriesData || [], seasonCounts);
        this.setCache(cacheKey, mapped);
        return mapped;
      } catch (err) {
        logger.error('Unexpected error fetching series with free episodes:', err);
        const result: Serie[] = [];
        this.setCache(cacheKey, result);
        return result;
      }
    })();

    this.setCache(cacheKey, fetchPromise);
    return fetchPromise;
  }

  // INTERNAL: Pagination helper to fetch all rows beyond Supabase's 1000-row limit
  private static async fetchAllRowsPaginated<T>(
    baseQuery: any,
    batchSize: number = 1000
  ): Promise<T[]> {
    const allResults: T[] = [];
    let offset = 0;
    let hasMore = true;

    logger.info(`📄 [PAGINATION] Starting paginated fetch with batch size ${batchSize}...`);

    while (hasMore) {
      const { data, error } = await baseQuery.range(offset, offset + batchSize - 1);

      if (error) {
        logger.error(`📄 [PAGINATION] Error fetching batch at offset ${offset}:`, error);
        throw error;
      }

      if (!data || data.length === 0) {
        hasMore = false;
        break;
      }

      logger.info(`📄 [PAGINATION] Fetched batch: ${data.length} rows (offset: ${offset})`);
      allResults.push(...data);

      // If we got fewer rows than batch size, we've reached the end
      if (data.length < batchSize) {
        hasMore = false;
      } else {
        offset += batchSize;
      }
    }

    logger.info(`📄 [PAGINATION] ✅ Completed: fetched ${allResults.length} total rows`);
    return allResults;
  }

  static async fetchCompleteEpisodesData(campaignCountriesLanguagesId: string, seriesId: string, freeEpisodesCount: number = 3): Promise<Episode[]> {
    try {
      const startTime = Date.now();
      logger.info(`[FULL-PREFETCH] Fetching complete episodes data for series ${seriesId}...`);

      const languageCode = campaignCache.getLanguageCode(campaignCountriesLanguagesId);
      const numericSeriesId = parseInt(seriesId);

      const unifiedEpisodes = await unifiedContentService.getEpisodesWithFreeStatus(
        numericSeriesId,
        languageCode,
        freeEpisodesCount
      );

      if (!unifiedEpisodes || unifiedEpisodes.length === 0) {
        logger.info(`[FULL-PREFETCH] No episodes found for series ${seriesId}`);
        return [];
      }

      const episodes: Episode[] = unifiedEpisodes.map(ep => ({
        id: ep.episode_id.toString(),
        title: ep.title,
        description: ep.description || `Episode ${ep.episode_position} description`,
        thumbnail: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176',
        duration: ep.duration || 900,
        saisonId: ep.season_id.toString(),
        episodeNumber: ep.episode_position,
        is_free: ep.isFree,
        video_url: ep.url_streaming_no_drm,
        url_streaming_drm: ep.url_streaming_drm,
        seriesId: seriesId,
        seasonNumber: ep.season_position || 1,
        seasonId: ep.season_id.toString()
      }));

      const duration = Date.now() - startTime;
      logger.info(`[FULL-PREFETCH] Fetched ${episodes.length} episodes in ${duration}ms`);

      return episodes;
    } catch (err) {
      logger.error('Unexpected error fetching complete episodes data:', err);
      return [];
    }
  }

  private static async fetchEpisodesFromDatabase(campaignCountriesLanguagesId: string, seriesId: string, freeEpisodesCount: number = 3): Promise<Episode[]> {
    try {
      logger.info(`[PREFETCH] Fetching episodes from unified tables for series ${seriesId}...`);

      const languageCode = campaignCache.getLanguageCode(campaignCountriesLanguagesId);
      const numericSeriesId = parseInt(seriesId);

      const unifiedEpisodes = await unifiedContentService.getEpisodesWithFreeStatus(
        numericSeriesId,
        languageCode,
        freeEpisodesCount
      );

      if (!unifiedEpisodes || unifiedEpisodes.length === 0) {
        logger.info('[PREFETCH] No episodes found for series');
        return [];
      }

      const episodes: Episode[] = unifiedEpisodes.map(ep => ({
        id: ep.episode_id.toString(),
        title: ep.title,
        description: ep.description || `Episode ${ep.episode_position} description`,
        thumbnail: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176',
        duration: ep.duration || 900,
        saisonId: ep.season_id.toString(),
        episodeNumber: ep.episode_position,
        is_free: ep.isFree,
        video_url: ep.url_streaming_no_drm,
        url_streaming_drm: ep.url_streaming_drm,
        seriesId: seriesId,
        seasonNumber: ep.season_position || 1,
        seasonId: ep.season_id.toString()
      }));

      logger.info(`[PREFETCH] Fetched ${episodes.length} episodes from unified tables`);
      return episodes;
    } catch (err: any) {
      logger.error('[PREFETCH] Unexpected error fetching series episodes:', err);
      return [];
    }
  }

  // PUBLIC: Fetch episodes for prefetching (used by homepage)
  // This is the ONLY method that should query the database for episodes
  static async fetchEpisodesForPrefetch(campaignCountriesLanguagesId: string | null, seriesId: string): Promise<Episode[]> {
    if (!campaignCountriesLanguagesId) {
      return [];
    }

    // Check cache first to avoid unnecessary database queries
    const cachedEpisodes = await episodesCacheService.getEpisodes(campaignCountriesLanguagesId, seriesId);
    if (cachedEpisodes) {
      logger.info(`📺 [PREFETCH] Using cached episodes for series ${seriesId} (${cachedEpisodes.length} episodes)`);
      return cachedEpisodes;
    }

    // Fetch from database and cache the result
    const episodes = await this.fetchEpisodesFromDatabase(campaignCountriesLanguagesId, seriesId);

    if (episodes.length > 0) {
      await episodesCacheService.setEpisodes(campaignCountriesLanguagesId, seriesId, episodes);
    }

    return episodes;
  }

  private static async collectPrioritySeriesIds(
    campaignCountriesLanguagesId: string
  ): Promise<string[]> {
    const priorityIds: string[] = [];

    try {
      const top3 = await this.getHomepageTop3(campaignCountriesLanguagesId);
      for (const serie of top3) {
        if (serie.id) priorityIds.push(serie.id);
      }
    } catch {
    }

    try {
      if (supabase) {
        const { data: firstRubric } = await supabase
          .from('contents_rubrics')
          .select('id_rubric')
          .eq('campaign_countries_languages_id', campaignCountriesLanguagesId)
          .limit(1)
          .maybeSingle();

        if (firstRubric) {
          const { data: rubricSeries } = await supabase
            .from('contents_series_rubrics')
            .select('serie_id')
            .eq('id_rubric', firstRubric.id_rubric)
            .eq('campaign_countries_languages_id', campaignCountriesLanguagesId);

          if (rubricSeries) {
            for (const item of rubricSeries) {
              priorityIds.push(item.serie_id.toString());
            }
          }
        }
      }
    } catch {
    }

    return [...new Set(priorityIds)];
  }

  static async fetchAllEpisodesForMultipleSeries(
    campaignCountriesLanguagesId: string,
    seriesIds: string[],
    freeEpisodesCount: number = 3
  ): Promise<Map<string, Episode[]>> {
    const episodesMap = new Map<string, Episode[]>();

    if (seriesIds.length === 0) {
      return episodesMap;
    }

    const languageCode = campaignCache.getLanguageCode(campaignCountriesLanguagesId);
    const numericSeriesIds = seriesIds.map(id => parseInt(id));

    const unifiedEpisodesMap = await unifiedContentService.fetchAllEpisodesForMultipleSeries(
      numericSeriesIds,
      languageCode,
      freeEpisodesCount
    );

    unifiedEpisodesMap.forEach((episodes, numericSeriesId) => {
      const seriesIdStr = numericSeriesId.toString();
      const transformedEpisodes: Episode[] = episodes.map(ep => ({
        id: ep.episode_id.toString(),
        title: ep.title,
        description: ep.description || `Episode ${ep.episode_position} description`,
        thumbnail: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176',
        duration: ep.duration || 900,
        saisonId: ep.season_id.toString(),
        episodeNumber: ep.episode_position,
        is_free: ep.isFree,
        video_url: ep.url_streaming_no_drm,
        url_streaming_drm: ep.url_streaming_drm,
        seriesId: seriesIdStr,
        seasonNumber: ep.season_position || 1,
        seasonId: ep.season_id.toString()
      }));

      episodesMap.set(seriesIdStr, transformedEpisodes);
    });

    return episodesMap;
  }

  // Get all episodes for a series (for playlist creation)
  // Falls back to database when cache misses occur
  // Note: is_free is recalculated dynamically based on current freeEpisodesCount from campaign config
  static async getAllEpisodesForSeries(campaignCountriesLanguagesId: string | null, seriesId: string): Promise<Episode[]> {
    const timestamp = new Date().toISOString();

    if (!campaignCountriesLanguagesId) {
      logger.info('ContentService.getAllEpisodesForSeries: No campaign ID, returning empty results');
      return [];
    }

    logger.info(`🔍 [${timestamp}] getAllEpisodesForSeries called for series ${seriesId}`);

    const deduplicateEpisodes = (episodes: Episode[]): Episode[] => {
      const seen = new Set<string>();
      const deduplicated: Episode[] = [];
      for (const episode of episodes) {
        if (!seen.has(episode.id)) {
          seen.add(episode.id);
          deduplicated.push(episode);
        } else {
          logger.warn(`🔍 [${timestamp}] ⚠️ DUPLICATE DETECTED: Episode ${episode.id} (${episode.title}) in series ${seriesId}`);
        }
      }
      return deduplicated;
    };

    const recalculateIsFree = (episodes: Episode[]): Episode[] => {
      const freeEpisodesCount = campaignCache.getFreeEpisodesCount(campaignCountriesLanguagesId!);

      const sortedEpisodes = [...episodes].sort((a, b) => {
        const seasonA = a.seasonNumber ?? 1;
        const seasonB = b.seasonNumber ?? 1;
        if (seasonA !== seasonB) return seasonA - seasonB;
        const episodeA = a.episodeNumber ?? 1;
        const episodeB = b.episodeNumber ?? 1;
        return episodeA - episodeB;
      });

      const freeEpisodeIds = new Set(
        sortedEpisodes.slice(0, freeEpisodesCount).map(ep => ep.id)
      );

      return episodes.map(episode => ({
        ...episode,
        is_free: freeEpisodeIds.has(episode.id)
      }));
    };

    // Step 1: Check cache first
    const cachedEpisodes = await episodesCacheService.getEpisodes(campaignCountriesLanguagesId, seriesId);
    if (cachedEpisodes) {
      logger.info(`🔍 [${timestamp}] ✅ CACHE HIT: Using cached episodes for series ${seriesId} (${cachedEpisodes.length} episodes)`);
      const deduplicated = deduplicateEpisodes(cachedEpisodes);
      if (deduplicated.length !== cachedEpisodes.length) {
        logger.warn(`🔍 [${timestamp}] ⚠️ Removed ${cachedEpisodes.length - deduplicated.length} duplicate(s) from cached episodes`);
      }
      return recalculateIsFree(deduplicated);
    }

    // Step 2: Cache miss - fall back to database
    logger.info(`🔍 [${timestamp}] ❌ CACHE MISS: Fetching episodes from database for series ${seriesId}`);

    const dbStartTime = Date.now();
    const episodes = await this.fetchEpisodesFromDatabase(campaignCountriesLanguagesId, seriesId);
    const dbDuration = Date.now() - dbStartTime;

    if (episodes.length > 0) {
      logger.info(`🔍 [${timestamp}] ✅ DATABASE FALLBACK: Fetched ${episodes.length} episodes from database in ${dbDuration}ms`);

      const deduplicated = deduplicateEpisodes(episodes);
      if (deduplicated.length !== episodes.length) {
        logger.warn(`🔍 [${timestamp}] ⚠️ Removed ${episodes.length - deduplicated.length} duplicate(s) from database episodes`);
      }

      // Step 3: Cache the deduplicated results for future use
      await episodesCacheService.setEpisodes(campaignCountriesLanguagesId, seriesId, deduplicated);
      logger.info(`🔍 [${timestamp}] 💾 Cached ${deduplicated.length} episodes for future access`);

      return recalculateIsFree(deduplicated);
    } else {
      logger.info(`🔍 [${timestamp}] ⚠️ DATABASE FALLBACK: No episodes found in database for series ${seriesId}`);
    }

    return episodes;
  }

  private static exclusiveSerieIdsCache: Map<string, Set<number> | null> = new Map();

  private static async getExclusiveHekayaSerieIds(campaignCountriesLanguagesId: string): Promise<Set<number> | null> {
    const cached = ContentService.exclusiveSerieIdsCache.get(campaignCountriesLanguagesId);
    if (cached !== undefined) return cached;

    if (!supabase) return null;

    const TOP_CAROUSEL_RUBRIC_IDS = [318217, 319084];

    try {
      const { data: rubrics } = await supabase
        .from('contents_rubrics')
        .select('id_rubric')
        .eq('campaign_countries_languages_id', campaignCountriesLanguagesId)
        .in('id_rubric', TOP_CAROUSEL_RUBRIC_IDS)
        .limit(1);

      if (!rubrics || rubrics.length === 0) {
        ContentService.exclusiveSerieIdsCache.set(campaignCountriesLanguagesId, null);
        return null;
      }

      const { data: seriesRubrics } = await supabase
        .from('contents_series_rubrics')
        .select('serie_id')
        .eq('campaign_countries_languages_id', campaignCountriesLanguagesId)
        .eq('id_rubric', rubrics[0].id_rubric);

      const ids = new Set<number>((seriesRubrics || []).map((r: { serie_id: number }) => r.serie_id));
      ContentService.exclusiveSerieIdsCache.set(campaignCountriesLanguagesId, ids);
      return ids;
    } catch {
      return null;
    }
  }

  static async getFirstEpisodesOfAllSeries(campaignCountriesLanguagesId: string | null, freeEpisodesCount: number = 3): Promise<Episode[]> {
    if (!campaignCountriesLanguagesId) {
      logger.info('ContentService.getFirstEpisodesOfAllSeries: No campaign ID, returning empty results');
      return [];
    }

    if (!supabase) {
      logger.warn('Supabase not configured');
      return [];
    }

    try {
      const languageCode = campaignCache.getLanguageCode(campaignCountriesLanguagesId);

      const firstEpisodesMap = await unifiedContentService.getFirstEpisodesOfAllSeries(languageCode);

      // Filter to only series from "Séries exclusives Hekaya" rubric
      const exclusiveSerieIds = await ContentService.getExclusiveHekayaSerieIds(campaignCountriesLanguagesId);
      if (exclusiveSerieIds !== null) {
        for (const seriesId of Array.from(firstEpisodesMap.keys())) {
          if (!exclusiveSerieIds.has(seriesId)) {
            firstEpisodesMap.delete(seriesId);
          }
        }
      }

      if (firstEpisodesMap.size === 0) {
        logger.info('No first episodes found');
        return [];
      }

      const seriesIds = Array.from(firstEpisodesMap.keys());

      const { data: seriesData } = await supabase
        .from('contents_series')
        .select('serie_id, title, url_covers')
        .eq('campaign_countries_languages_id', campaignCountriesLanguagesId)
        .in('serie_id', seriesIds);

      const seriesCoversMap = new Map<number, { title: string; url_covers: string }>();
      (seriesData || []).forEach(s => {
        seriesCoversMap.set(s.serie_id, { title: s.title, url_covers: s.url_covers });
      });

      const episodes: Episode[] = [];
      firstEpisodesMap.forEach((episode, seriesId) => {
        const seriesCover = seriesCoversMap.get(seriesId);
        const videoUrl = episode.url_streaming_no_drm || episode.url_streaming_drm || undefined;
        episodes.push({
          id: episode.episode_id.toString(),
          title: episode.title,
          description: episode.description || `Episode ${episode.episode_position} description`,
          thumbnail: addImageResizeParams(seriesCover?.url_covers, ImageSizePresets.forYouSeries, `For You: ${seriesCover?.title || 'Unknown'}`),
          duration: episode.duration || 900,
          saisonId: episode.season_id.toString(),
          episodeNumber: episode.episode_position,
          is_free: freeEpisodesCount >= 1,
          video_url: videoUrl,
          url_streaming_drm: episode.url_streaming_drm,
          seriesId: seriesId.toString(),
          seasonNumber: episode.season_position || 1,
          seasonId: episode.season_id.toString()
        });
      });

      return episodes;
    } catch (err) {
      logger.error('Unexpected error fetching first episodes:', err);
      return [];
    }
  }

  private static async fetchSeasonCounts(seriesIds: number[]): Promise<Map<number, number>> {
    const seasonCountMap = new Map<number, number>();
    if (seriesIds.length === 0) return seasonCountMap;

    const { data } = await supabase
      .from('unified_contents_series_episodes')
      .select('series_id, season_position')
      .in('series_id', seriesIds);

    if (data) {
      const seasonsBySeriesId = new Map<number, Set<number>>();
      for (const ep of data) {
        if (!seasonsBySeriesId.has(ep.series_id)) {
          seasonsBySeriesId.set(ep.series_id, new Set());
        }
        seasonsBySeriesId.get(ep.series_id)!.add(ep.season_position);
      }
      for (const [seriesId, seasons] of seasonsBySeriesId) {
        seasonCountMap.set(seriesId, seasons.size);
      }
    }

    return seasonCountMap;
  }

  private static mapSeriesToInterface(seriesData: DatabaseSerie[], seasonCounts?: Map<number, number>): Serie[] {
    return seriesData.map((serie: DatabaseSerie) => ({
      id: serie.serie_id.toString(),
      title: serie.title,
      description: serie.description || 'No description available',
      thumbnail: addImageResizeParams(serie.url_covers, ImageSizePresets.serieCardVertical, `Serie Card: ${serie.title}`),
      thumbnailLandscape: serie.url_covers_landscape
        ? addImageResizeParams(serie.url_covers_landscape, ImageSizePresets.serieCardLandscape, `Serie Card Landscape: ${serie.title}`)
        : undefined,
      rubriqueId: '1',
      rating: 4.5 + Math.random() * 0.5,
      totalSeasons: seasonCounts?.get(serie.serie_id) || 1,
      isPopular: Math.random() > 0.7,
      isNew: Math.random() > 0.8,
      isTrending: Math.random() > 0.6
    }));
  }

  static async getSeriesInfoForPlayer(campaignCountriesLanguagesId: string | null, seriesId: string | number): Promise<{
    title: string;
    description: string;
    thumbnail: string;
    totalSeasons: number;
    totalEpisodes: number;
  } | null> {
    if (!campaignCountriesLanguagesId || !seriesId) {
      return null;
    }

    const normalizedSeriesId = String(seriesId);

    if (!supabase) {
      return null;
    }

    try {
      const { seriesMetadataCacheService } = await import('@/services/seriesMetadataCacheService');
      const cached = await seriesMetadataCacheService.getSeriesMetadata(campaignCountriesLanguagesId, normalizedSeriesId);

      let seriesData: any;

      if (cached) {
        logger.info(`🎬 [GET-SERIES-INFO] Using cached series metadata for: ${normalizedSeriesId}`);
        seriesData = {
          title: cached.title,
          description: cached.description,
          url_covers: cached.url_covers
        };
      } else {
        logger.info(`🎬 [GET-SERIES-INFO] Fetching series metadata from database for: ${normalizedSeriesId}`);
        const { data: fetchedData, error: seriesError } = await supabase
          .from('contents_series')
          .select('title, description, url_covers, serie_id')
          .eq('campaign_countries_languages_id', campaignCountriesLanguagesId)
          .eq('serie_id', normalizedSeriesId)
          .single();

        if (seriesError || !fetchedData) {
          logger.error('Error fetching series info:', seriesError);
          return null;
        }

        await seriesMetadataCacheService.setSeriesMetadata(campaignCountriesLanguagesId, {
          serie_id: fetchedData.serie_id || parseInt(normalizedSeriesId),
          title: fetchedData.title,
          description: fetchedData.description,
          url_covers: fetchedData.url_covers
        });

        seriesData = fetchedData;
      }

      const { data: fetchedEpisodes, error: episodesError } = await supabase
        .from('unified_contents_series_episodes')
        .select('season_position, episode_id')
        .eq('series_id', parseInt(normalizedSeriesId));

      if (episodesError) {
        logger.error('Error fetching episodes for series info:', episodesError);
        return null;
      }

      if (!fetchedEpisodes || fetchedEpisodes.length === 0) {
        logger.error('No episodes data available for series info');
        return null;
      }

      const uniqueSeasons = new Set(fetchedEpisodes.map(ep => ep.season_position));
      const totalSeasons = uniqueSeasons.size;
      const totalEpisodes = fetchedEpisodes.length;

      return {
        title: seriesData.title,
        description: seriesData.description,
        thumbnail: addImageResizeParams(seriesData.url_covers, ImageSizePresets.playerSeriesInfo, `Player Info: ${seriesData.title}`),
        totalSeasons,
        totalEpisodes,
      };
    } catch (err) {
      logger.error('Unexpected error fetching series info for player:', err);
      return null;
    }
  }

  // Get user's viewing progress for a series and determine resume episode
  static async getResumeEpisodeForSeries(
    campaignCountriesLanguagesId: string | null,
    seriesId: string,
    smartuserId: string
  ): Promise<{
    episodeId: string;
    startTimeSeconds: number;
    completionPercentage: number;
  } | null> {
    logger.info('🎬 [RESUME-START] Getting resume episode for series:', {
      campaignCountriesLanguagesId,
      seriesId,
      smartuserId,
      platform: require('react-native').Platform.OS
    });

    if (!campaignCountriesLanguagesId || !seriesId || !smartuserId) {
      logger.info('❌ [RESUME-ERROR] Missing required parameters:', {
        hasCampaignId: !!campaignCountriesLanguagesId,
        hasSeriesId: !!seriesId,
        hasSmartuserId: !!smartuserId
      });
      return null;
    }

    const sessionId = Math.random().toString(36).substring(7);
    logger.info(`📦 [RESUME-${sessionId}] 🔍 STEP 1: Checking cache for resume episode...`);
    logger.info(`📦 [RESUME-${sessionId}] Series ID: "${seriesId}" (type: ${typeof seriesId})`);

    const cacheStartTime = Date.now();
    const cachedResume = await viewingProgressCache.getResumeEpisode(seriesId);
    const cacheDuration = Date.now() - cacheStartTime;

    if (cachedResume) {
      logger.info(`✅ [RESUME-${sessionId}] CACHE HIT! Resume episode retrieved from cache in ${cacheDuration}ms:`, cachedResume);
      return cachedResume;
    }

    logger.info(`❌ [RESUME-${sessionId}] CACHE MISS - No resume found in cache (took ${cacheDuration}ms)`);
    logger.info(`📊 [RESUME-${sessionId}] 🔍 STEP 2: Querying database for resume position...`);

    // Fallback: Query database if cache miss
    try {
      logger.info(`📊 [RESUME-${sessionId}] Fetching all episodes for series...`);
      const dbStartTime = Date.now();

      // Get all episodes for this series
      const allEpisodes = await this.getAllEpisodesForSeries(campaignCountriesLanguagesId, seriesId);
      if (!allEpisodes || allEpisodes.length === 0) {
        logger.info(`❌ [RESUME-${sessionId}] No episodes found for series`);
        return null;
      }

      const episodeIds = allEpisodes.map(ep => parseInt(ep.id));
      logger.info(`📊 [RESUME-${sessionId}] Found ${allEpisodes.length} episodes for series`);
      logger.info(`📊 [RESUME-${sessionId}] Episode IDs to check:`, episodeIds.slice(0, 5), episodeIds.length > 5 ? '...' : '');

      // Query viewing progress for all episodes in this series
      logger.info(`📊 [RESUME-${sessionId}] Querying user_viewing_progress table with filters:`, {
        smartuser_id: smartuserId,
        content_type: 'episode',
        episodeCount: episodeIds.length,
        completionRange: '0% < progress < 90%'
      });

      const { data: progressData, error: progressError } = await supabase
        .from('user_viewing_progress')
        .select('content_id, completion_percentage, updated_at')
        .eq('smartuser_id', smartuserId)
        .eq('content_type', 'episode')
        .in('content_id', episodeIds)
        .lt('completion_percentage', 90)
        .gt('completion_percentage', 0)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (progressError) {
        logger.error(`❌ [RESUME-${sessionId}] Database query error:`, progressError);
        return null;
      }

      const dbDuration = Date.now() - dbStartTime;

      if (!progressData) {
        logger.info(`📊 [RESUME-${sessionId}] No viewing progress found in database (query took ${dbDuration}ms)`);
        return null;
      }

      logger.info(`✅ [RESUME-${sessionId}] Found progress data from DB:`, {
        content_id: progressData.content_id,
        content_id_type: typeof progressData.content_id,
        completion_percentage: progressData.completion_percentage,
        updated_at: progressData.updated_at
      });

      // Find the episode details
      const resumeEpisode = allEpisodes.find(ep => parseInt(ep.id) === progressData.content_id);
      if (!resumeEpisode) {
        logger.info(`❌ [RESUME-${sessionId}] Episode not found in episode list`);
        return null;
      }

      logger.info(`✅ [RESUME-${sessionId}] Matched episode details:`, {
        id: resumeEpisode.id,
        id_type: typeof resumeEpisode.id,
        title: resumeEpisode.title,
        seasonNumber: resumeEpisode.seasonNumber,
        episodeNumber: resumeEpisode.episodeNumber,
        saisonId: resumeEpisode.saisonId,
        duration: resumeEpisode.duration
      });

      // Calculate resume time (slightly before current position to show context)
      const duration = resumeEpisode.duration || 900;
      const startTimeSeconds = Math.max(0, Math.floor((progressData.completion_percentage / 100) * duration) - 5);

      const result = {
        episodeId: resumeEpisode.id,
        startTimeSeconds,
        completionPercentage: progressData.completion_percentage
      };

      logger.info(`✅ [RESUME-${sessionId}] Resume position calculated (query took ${dbDuration}ms):`, result);

      // Update cache with database result for future requests
      logger.info(`📦 [RESUME-${sessionId}] 🔍 STEP 3: Building cache entry...`);

      const cacheEntry: CachedViewingProgress = {
        episodeId: resumeEpisode.id,
        completionPercentage: progressData.completion_percentage,
        isCompleted: progressData.completion_percentage >= 90,
        updatedAt: progressData.updated_at,
        episodeTitle: resumeEpisode.title,
        episodeDescription: resumeEpisode.description,
        episodeDuration: resumeEpisode.duration,
        episodePosition: resumeEpisode.episodeNumber,
        seasonPosition: resumeEpisode.seasonNumber,
        seasonId: resumeEpisode.saisonId,
        seriesId: seriesId,
        videoUrl: resumeEpisode.video_url,
      };

      logger.info(`📦 [RESUME-${sessionId}] Cache entry to be stored:`, {
        episodeId: cacheEntry.episodeId,
        episodeIdType: typeof cacheEntry.episodeId,
        seriesId: cacheEntry.seriesId,
        seriesIdType: typeof cacheEntry.seriesId,
        completionPercentage: cacheEntry.completionPercentage,
        seasonPosition: cacheEntry.seasonPosition,
        episodePosition: cacheEntry.episodePosition,
        episodeDuration: cacheEntry.episodeDuration,
        seasonId: cacheEntry.seasonId,
        updatedAt: cacheEntry.updatedAt
      });

      logger.info(`📦 [RESUME-${sessionId}] Calling viewingProgressCache.updateEpisodeProgress()...`);
      await viewingProgressCache.updateEpisodeProgress(cacheEntry);
      logger.info(`✅ [RESUME-${sessionId}] Cache update completed`);

      logger.info(`📦 [RESUME-${sessionId}] 🔍 STEP 4: Verifying cache was updated...`);
      const verification = await viewingProgressCache.getResumeEpisode(seriesId);
      if (verification) {
        logger.info(`✅ [RESUME-${sessionId}] VERIFICATION SUCCESS: Resume episode now found in cache:`, verification);
      } else {
        logger.info(`❌ [RESUME-${sessionId}] VERIFICATION FAILED: Resume episode NOT found in cache after update!`);
      }

      return result;
    } catch (err) {
      logger.error('❌ [RESUME-DB] Unexpected error querying database:', err);
      return null;
    }
  }
}